import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { 
  Search, 
  RefreshCw, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  GraduationCap,
  Eye,
  Save
} from "lucide-react";

interface TrainingExample {
  id: string;
  original_text: string;
  ampel_result: string;
  ai_headline: string | null;
  ai_explanation: string | null;
  user_feedback: string | null;
  user_feedback_at: string | null;
  admin_verified: boolean;
  admin_corrected_result: string | null;
  admin_notes: string | null;
  use_for_training: boolean;
  training_category: string | null;
  input_length: number | null;
  created_at: string;
}

const CATEGORIES = [
  { value: "phishing", label: "Phishing" },
  { value: "fake-shop", label: "Fake-Shop" },
  { value: "enkeltrick", label: "Enkeltrick" },
  { value: "gewinnspiel", label: "Gewinnspiel" },
  { value: "serioes", label: "Seriös" },
  { value: "sonstiges", label: "Sonstiges" }
];

export function KlartextTrainingAdmin() {
  const { toast } = useToast();
  const [examples, setExamples] = useState<TrainingExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "verified" | "training">("pending");
  const [selectedExample, setSelectedExample] = useState<TrainingExample | null>(null);
  const [editForm, setEditForm] = useState({
    admin_corrected_result: "",
    admin_notes: "",
    training_category: "",
    use_for_training: false
  });
  const [saving, setSaving] = useState(false);

  const fetchExamples = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("klartext_training_examples")
        .select("*")
        .order("created_at", { ascending: false });

      if (filter === "pending") {
        query = query.eq("admin_verified", false).not("user_feedback", "is", null);
      } else if (filter === "verified") {
        query = query.eq("admin_verified", true);
      } else if (filter === "training") {
        query = query.eq("use_for_training", true);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      setExamples(data || []);
    } catch (err) {
      console.error("Error fetching examples:", err);
      toast({
        title: "Fehler",
        description: "Trainingsbeispiele konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamples();
  }, [filter]);

  const openEditDialog = (example: TrainingExample) => {
    setSelectedExample(example);
    setEditForm({
      admin_corrected_result: example.admin_corrected_result || example.ampel_result,
      admin_notes: example.admin_notes || "",
      training_category: example.training_category || "",
      use_for_training: example.use_for_training
    });
  };

  const saveExample = async () => {
    if (!selectedExample) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("klartext_training_examples")
        .update({
          admin_verified: true,
          admin_verified_at: new Date().toISOString(),
          admin_corrected_result: editForm.admin_corrected_result || null,
          admin_notes: editForm.admin_notes || null,
          training_category: editForm.training_category || null,
          use_for_training: editForm.use_for_training
        })
        .eq("id", selectedExample.id);

      if (error) throw error;

      toast({
        title: "Gespeichert!",
        description: editForm.use_for_training 
          ? "Beispiel wird für Training verwendet" 
          : "Beispiel wurde verifiziert"
      });
      setSelectedExample(null);
      fetchExamples();
    } catch (err) {
      console.error("Save error:", err);
      toast({
        title: "Fehler beim Speichern",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getAmpelBadge = (result: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode }> = {
      gruen: { color: "bg-green-100 text-green-700", icon: <CheckCircle className="w-3 h-3" /> },
      gelb: { color: "bg-yellow-100 text-yellow-700", icon: <AlertTriangle className="w-3 h-3" /> },
      rot: { color: "bg-red-100 text-red-700", icon: <XCircle className="w-3 h-3" /> }
    };
    const c = config[result] || config.gelb;
    return (
      <Badge className={`${c.color} gap-1`}>
        {c.icon}
        {result}
      </Badge>
    );
  };

  const getFeedbackBadge = (feedback: string | null) => {
    if (!feedback) return null;
    return feedback === "correct" ? (
      <Badge className="bg-green-100 text-green-700 gap-1">
        <ThumbsUp className="w-3 h-3" /> Richtig
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-700 gap-1">
        <ThumbsDown className="w-3 h-3" /> Falsch
      </Badge>
    );
  };

  const filteredExamples = examples.filter(ex =>
    ex.original_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (ex.ai_headline?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
    (ex.training_category?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: examples.length,
    pendingReview: examples.filter(e => !e.admin_verified && e.user_feedback).length,
    forTraining: examples.filter(e => e.use_for_training).length,
    incorrectFeedback: examples.filter(e => e.user_feedback === "incorrect").length
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Gesamt</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-yellow-600">{stats.pendingReview}</p>
            <p className="text-sm text-muted-foreground">Zu prüfen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{stats.forTraining}</p>
            <p className="text-sm text-muted-foreground">Für Training</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-red-600">{stats.incorrectFeedback}</p>
            <p className="text-sm text-muted-foreground">Als falsch markiert</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="border-b bg-slate-50 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              Training & Feedback
            </CardTitle>
            <div className="flex-1 flex flex-wrap gap-2">
              <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="pending">Zu prüfen</SelectItem>
                  <SelectItem value="verified">Verifiziert</SelectItem>
                  <SelectItem value="training">Für Training</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={fetchExamples} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            </div>
          ) : filteredExamples.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Keine Beispiele gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[300px]">Text (Vorschau)</TableHead>
                    <TableHead>AI-Ergebnis</TableHead>
                    <TableHead>Feedback</TableHead>
                    <TableHead>Kategorie</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExamples.map((ex) => (
                    <TableRow key={ex.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-xs">
                        {ex.original_text.substring(0, 80)}...
                      </TableCell>
                      <TableCell>{getAmpelBadge(ex.ampel_result)}</TableCell>
                      <TableCell>{getFeedbackBadge(ex.user_feedback)}</TableCell>
                      <TableCell>
                        {ex.training_category && (
                          <Badge variant="outline">{ex.training_category}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {ex.use_for_training ? (
                          <Badge className="bg-purple-100 text-purple-700">Training</Badge>
                        ) : ex.admin_verified ? (
                          <Badge className="bg-blue-100 text-blue-700">Verifiziert</Badge>
                        ) : (
                          <Badge variant="outline">Offen</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(ex.created_at).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openEditDialog(ex)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Beispiel bearbeiten</DialogTitle>
                            </DialogHeader>
                            {selectedExample && (
                              <div className="space-y-4">
                                {/* Original Text */}
                                <div>
                                  <label className="text-sm font-medium">Original-Text</label>
                                  <div className="mt-1 p-3 bg-slate-50 rounded-lg text-sm max-h-40 overflow-y-auto">
                                    {selectedExample.original_text}
                                  </div>
                                </div>

                                {/* AI Result */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium">KI-Ergebnis</label>
                                    <div className="mt-1">{getAmpelBadge(selectedExample.ampel_result)}</div>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium">Nutzer-Feedback</label>
                                    <div className="mt-1">{getFeedbackBadge(selectedExample.user_feedback) || <span className="text-muted-foreground text-sm">Keins</span>}</div>
                                  </div>
                                </div>

                                {selectedExample.ai_headline && (
                                  <div>
                                    <label className="text-sm font-medium">KI-Überschrift</label>
                                    <p className="mt-1 text-sm">{selectedExample.ai_headline}</p>
                                  </div>
                                )}

                                {/* Admin Correction */}
                                <div>
                                  <label className="text-sm font-medium">Korrigiertes Ergebnis</label>
                                  <Select 
                                    value={editForm.admin_corrected_result} 
                                    onValueChange={(v) => setEditForm({...editForm, admin_corrected_result: v})}
                                  >
                                    <SelectTrigger className="mt-1">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="gruen">🟢 Grün (Seriös)</SelectItem>
                                      <SelectItem value="gelb">🟡 Gelb (Verdächtig)</SelectItem>
                                      <SelectItem value="rot">🔴 Rot (Betrug)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Category */}
                                <div>
                                  <label className="text-sm font-medium">Kategorie</label>
                                  <Select 
                                    value={editForm.training_category} 
                                    onValueChange={(v) => setEditForm({...editForm, training_category: v})}
                                  >
                                    <SelectTrigger className="mt-1">
                                      <SelectValue placeholder="Kategorie wählen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {CATEGORIES.map(cat => (
                                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Notes */}
                                <div>
                                  <label className="text-sm font-medium">Admin-Notizen</label>
                                  <Textarea
                                    className="mt-1"
                                    value={editForm.admin_notes}
                                    onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                                    placeholder="Interne Notizen..."
                                    rows={2}
                                  />
                                </div>

                                {/* Use for Training */}
                                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                                  <input
                                    type="checkbox"
                                    id="useForTraining"
                                    checked={editForm.use_for_training}
                                    onChange={(e) => setEditForm({...editForm, use_for_training: e.target.checked})}
                                    className="w-5 h-5 rounded"
                                  />
                                  <label htmlFor="useForTraining" className="flex-1">
                                    <span className="font-medium">Für Training verwenden</span>
                                    <p className="text-sm text-muted-foreground">
                                      Dieses Beispiel wird bei zukünftigen Analysen als Referenz genutzt
                                    </p>
                                  </label>
                                </div>

                                <Button 
                                  onClick={saveExample} 
                                  disabled={saving}
                                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                                >
                                  {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                  )}
                                  Speichern & Verifizieren
                                </Button>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
